import { useContext, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Slide,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Container } from "@mui/system";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Controller, useForm } from "react-hook-form";
import { CheckCircle, Edit, Lock, Logout, Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import AuthContext from "../../context/AuthProvider";
import axios from "../../api/axios";
import ActiveSessions from "./active-sessions";
import ProfilePicture from "./profile-picture";
import { queryClient } from "../../App";
import DeleteMyAccount from "./deleteAccount";

function Profile() {
  const navigate = useNavigate();
  const {
    auth,
    setAuth,
    removeTenantSession,
    clearTenantContext,
    tenantSessions,
    currentTenant,
    clearAllTenantSessions,
    switchToTenant,
  } = useContext(AuthContext);

  const tenantSession = tenantSessions[tenantSessions.length - 1];

  // USESTATE
  const [expanded, setExpanded] = useState(false);
  const [sessionExpanded, setSessionExpanded] = useState(true);
  const [edit, setEdit] = useState(false);
  const [open, setOpen] = useState(false);
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [openDeleteAccount, setOpenDeleteAccount] = useState(false);
  const [openChangePhone, setOpenChangePhone] = useState(false);
  const [file, setFile] = useState("");

  // USEFORM HOOK
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "onChange",
  });

  // CUSTOM HOOK
  const axiosPrivate = useAxiosPrivate();

  // SNACKBAR
  const { enqueueSnackbar } = useSnackbar();

  // MUTATION & QUERY
  const { mutate, isLoading, data, error } = useMutation((formData) => {
    return axiosPrivate.put(`/profile`, formData);
  });

  const {
    isLoading: profileLoading,
    data: profile,
    isFetching,
    refetch,
  } = useQuery(
    ["profile"],
    () => {
      return axiosPrivate.get(`/profile`);
    },
    {
      refetchOnWindowFocus: false,
      enabled: false,
    }
  );

  const { mutate: mutateSignOut, isLoading: signOutIsLoading } = useMutation(
    (refreshToken) => {
      return axiosPrivate.post(
        "/logout",
        {
          refresh_token: refreshToken,
        },
        {
          withCredentials: true,
        }
      );
    }
  );

  // HELPERS
  // Logout from ALL sessions (all tenants)
  const signOutAll = async () => {
    const refreshToken = auth.refresh_token;
    return mutateSignOut(refreshToken, {
      onSuccess: () => {
        clearAllTenantSessions();
        setAuth({});
        try {
          localStorage.setItem("persist", false);
        } catch { }
        clearTenantContext(true);
        queryClient.clear();
        navigate("/login");
      },
      onError: (error) => {
        clearAllTenantSessions();
        setAuth({});
        try {
          localStorage.setItem("persist", false);
        } catch { }
        clearTenantContext(true);
        queryClient.clear();
        navigate("/login");
        console.error({
          error,
          msg: error?.response?.data?.error?.message,
          default: error?.message,
        });
        enqueueSnackbar(
          error?.response?.data?.error?.message ||
          error?.message ||
          "Network Error!",
          {
            variant: "error",
            TransitionComponent: Slide,
            preventDuplicate: true,
            autoHideDuration: 2000,
          }
        );
      },
    });
  };

  // Logout only from the CURRENT tenant
  const signOutCurrentTenant = async () => {
    const currentTenantId = currentTenant?.id || tenantSession?.tenant_id;
    if (!currentTenantId) {
      // Fallback: no tenant context, do full sign out
      return signOutAll();
    }

    const currentSession =
      tenantSessions.find((s) => s.tenant_id === currentTenantId) ||
      tenantSession;

    const refreshToken =
      currentSession?.auth?.refresh_token || auth.refresh_token;
    const remainingSessions = tenantSessions.filter(
      (s) => s.tenant_id !== currentTenantId
    );

    return mutateSignOut(refreshToken, {
      onSuccess: () => {
        removeTenantSession(currentTenantId);

        if (remainingSessions.length > 0) {
          // Switch to another logged-in tenant
          const next = remainingSessions[remainingSessions.length - 1];
          switchToTenant(next.tenant_id, false);
          queryClient.clear();
        } else {
          // No tenants left; behave like full sign-out
          clearAllTenantSessions();
          setAuth({});
          try {
            localStorage.setItem("persist", false);
          } catch { }
          clearTenantContext(true);
          queryClient.clear();
          navigate("/login");
        }
      },
      onError: (error) => {
        // Even if server-side logout fails, remove this tenant locally
        removeTenantSession(currentTenantId);

        if (remainingSessions.length > 0) {
          const next = remainingSessions[remainingSessions.length - 1];
          switchToTenant(next.tenant_id, false);
          queryClient.clear();
        } else {
          clearAllTenantSessions();
          setAuth({});
          try {
            localStorage.setItem("persist", false);
          } catch { }
          clearTenantContext(true);
          queryClient.clear();
          navigate("/login");
        }

        console.error({
          error,
          msg: error?.response?.data?.error?.message,
          default: error?.message,
        });
        enqueueSnackbar(
          error?.response?.data?.error?.message ||
          error?.message ||
          "Network Error!",
          {
            variant: "error",
            TransitionComponent: Slide,
            preventDuplicate: true,
            autoHideDuration: 2000,
          }
        );
      },
    });
  };

  const onSubmit = (profileData) => {
    const payload = { ...profileData };

    // Backend expects ISO datetime like "0001-01-01T00:00:00Z" (it rejects "YYYY-MM-DD").
    // Convert date-input value to datetime UTC at midnight; omit when unset.
    if (
      payload.birth_date === undefined ||
      payload.birth_date === null ||
      payload.birth_date === "" ||
      payload.birth_date === "0001-01-01T00:00:00Z" ||
      (typeof payload.birth_date === "string" && payload.birth_date.startsWith("0001-01-01"))
    ) {
      delete payload.birth_date;
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(payload.birth_date)) {
      payload.birth_date = `${payload.birth_date}T00:00:00Z`;
    }

    if (payload.gender === "" || payload.gender == null) {
      delete payload.gender;
    }

    const dataToBeSent =
      file !== "" ? { ...payload, profile_picture: file } : payload;
    mutate(dataToBeSent);
  };
  const handleChange = () => (event, isExpanded) => {
    setExpanded(isExpanded ? true : false);
  };
  const handleSessionPanelChange = () => (event, isExpanded) => {
    setSessionExpanded(isExpanded ? true : false);
  };

  const normalizeBirthDateForInput = (val) => {
    if (!val) return "";
    if (typeof val !== "string") return "";

    // Server uses "0001-01-01T00:00:00Z" as "unset" sentinel.
    if (val === "0001-01-01T00:00:00Z" || val.startsWith("0001-01-01")) return "";

    // Feed the date input with `YYYY-MM-DD` only (avoid timezone shifting).
    const match = val.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : "";
  };

  // USEEFFECT
  useEffect(() => {
    if (profile?.data?.data) {
      const d = profile.data.data;
      for (var key in d) {
        if (d.hasOwnProperty(key)) {
          const rawValue = d[key];
          const valueToSet =
            key === "birth_date" ? normalizeBirthDateForInput(rawValue) : rawValue;
          setValue(key, valueToSet);
        }
      }
      // API may omit empty fields; still register so edit mode shows inputs.
      setValue("gender", d.gender ?? "");
      setValue("phone", d.phone ?? "");
    }
  }, [setValue, profile]);

  useEffect(() => {
    if (data) {
      enqueueSnackbar(`Succesfully updated.`, {
        variant: "success",
      });
      refetch();
      setEdit(false);
      // Avoid clearing the whole cache; it triggers unnecessary refetches (e.g. devices).
      queryClient.invalidateQueries(["profile"]);
      queryClient.invalidateQueries(["active_sessions"]);
    }
  }, [data, enqueueSnackbar, refetch]);

  useEffect(() => {
    if (error) {
      if (error?.response?.data?.error?.field_error?.length > 0) {
        error?.response?.data?.error?.field_error?.map((msg) => {
          return enqueueSnackbar(msg.description || "Network Error!", {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 2000,
          });
        });
      } else {
        enqueueSnackbar(
          error?.response?.data?.error?.message ||
          error?.message ||
          "Network Error!",
          {
            variant: "error",
          }
        );
      }
    }
  }, [enqueueSnackbar, error]);

  // RENDER
  return profileLoading || isFetching ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "80vh",
      }}
    >
      <CircularProgress size={40} sx={{ color: "primary.main" }} />
    </Box>
  ) : (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: { xs: "auto", md: "100vh" },
        pt: { xs: 2, md: 6 },
        pb: { xs: 6, md: 10 },
        px: { xs: 0, sm: 0 },
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2, md: 3 } }}>
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {/* Profile Sidebar - The "Quick Glance" Card */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3, md: 4 },
                borderRadius: { xs: 3, md: 6 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                bgcolor: "background.paper",
                boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.04)",
                border: "1px solid", borderColor: "divider",
                position: { xs: "static", md: "sticky" },
                top: { md: 24 },
              }}
            >
              <Box sx={{ position: "relative", mb: 3 }}>
                <ProfilePicture
                  edit={edit}
                  isOpen={open}
                  setIsOpen={setOpen}
                  refetch={refetch}
                  imageSrc={
                    edit
                      ? file === ""
                        ? profile?.data?.data?.profile_picture
                        : file
                      : profile?.data?.data?.profile_picture
                  }
                  setFile={setFile}
                />
                {!edit && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      bgcolor: "primary.main",
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: theme => `3px solid ${theme.palette.background.paper}`,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    <CheckCircle sx={{ color: "white", fontSize: 16 }} />
                  </Box>
                )}
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: "secondary.main",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                  wordBreak: "break-word",
                  px: { xs: 0.5, sm: 0 },
                }}
              >
                {profile?.data?.data?.first_name} {profile?.data?.data?.last_name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  mb: 4,
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  maxWidth: "100%",
                }}
              >
                {profile?.data?.data?.email}
              </Typography>

              {!edit ? (
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Edit />}
                    onClick={() => setEdit(true)}
                    sx={{
                      borderRadius: 4,
                      textTransform: "none",
                      py: 1.5,
                      fontWeight: 700,
                      boxShadow: "0 8px 16px rgba(255, 170, 0, 0.2)",
                      "&:hover": {
                        boxShadow: "0 12px 24px rgba(255, 170, 0, 0.3)",
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    Edit Information
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="inherit"
                    startIcon={<Logout />}
                    onClick={signOutCurrentTenant}
                    disabled={signOutIsLoading}
                    sx={{
                      borderRadius: 4,
                      textTransform: "none",
                      py: 1.5,
                      color: "text.primary",
                      borderColor: "divider",
                      "&:hover": {
                        bgcolor: "action.hover",
                        borderColor: "rgba(0,0,0,0.2)",
                      },
                    }}
                  >
                    Log Out
                  </Button>
                </Box>
              ) : (
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSubmit(onSubmit)}
                    disabled={isLoading}
                    sx={{
                      borderRadius: 4,
                      textTransform: "none",
                      py: 1.5,
                      fontWeight: 700,
                    }}
                  >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : "Apply Changes"}
                  </Button>
                  <Button
                    fullWidth
                    variant="text"
                    color="inherit"
                    onClick={() => setEdit(false)}
                    sx={{ borderRadius: 4, textTransform: "none", fontWeight: 600 }}
                  >
                    Discard
                  </Button>
                </Box>
              )}

              <Divider sx={{ width: "100%", my: 4, opacity: 0.6 }} />

              <Typography variant="caption" sx={{ color: "text.disabled", mb: 2, display: "block" }}>
                Account Safety
              </Typography>
              <Button
                fullWidth
                color="error"
                variant="text"
                size="small"
                sx={{ textTransform: "none", fontWeight: 600, "&:hover": { bgcolor: "error.lighter" } }}
                onClick={() => setOpenDeleteAccount(true)}
              >
                Delete My Account
              </Button>
            </Paper>
          </Grid>

          {/* Main Content Area - Detailed Settings */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 6,
                  bgcolor: "background.paper",
                  boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.04)",
                  border: "1px solid", borderColor: "divider",
                }}
              >
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "secondary.main", letterSpacing: "-0.5px" }}>
                    Personal Details
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Management of your profile identity and contact information.
                  </Typography>
                </Box>

                <Box
                  component={edit ? "form" : "div"}
                  onSubmit={edit ? handleSubmit(onSubmit) : undefined}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <InfoField
                        label="First Name"
                        name="first_name"
                        value={profile?.data?.data?.first_name}
                        edit={edit}
                        control={control}
                        rules={{ required: "Required" }}
                        error={errors?.first_name}
                        placeholder="e.g. John"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InfoField
                        label="Middle Name"
                        name="middle_name"
                        value={profile?.data?.data?.middle_name}
                        edit={edit}
                        control={control}
                        placeholder="Optional"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InfoField
                        label="Last Name"
                        name="last_name"
                        value={profile?.data?.data?.last_name}
                        edit={edit}
                        control={control}
                        rules={{ required: "Required" }}
                        error={errors?.last_name}
                        placeholder="e.g. Doe"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InfoField
                        label="Gender"
                        name="gender"
                        value={profile?.data?.data?.gender}
                        edit={edit}
                        control={control}
                        select
                        options={[
                          { value: "", label: "Select gender" },
                          { value: "Male", label: "Male" },
                          { value: "Female", label: "Female" },
                        ]}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InfoField
                        label="Birth Date"
                        name="birth_date"
                        value={profile?.data?.data?.birth_date}
                        edit={edit}
                        control={control}
                        type="date"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InfoField
                        label="Location"
                        name="location"
                        value={profile?.data?.data?.location}
                        edit={edit}
                        control={control}
                        placeholder="e.g. Addis Ababa, Ethiopia"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1, opacity: 0.5 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InfoField
                        label="Primary Email"
                        name="email"
                        value={profile?.data?.data?.email}
                        edit={edit}
                        control={control}
                        disabled={true}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ position: "relative", pr: { xs: 5, sm: 6 } }}>
                        <InfoField
                          label="Phone Number"
                          name="phone"
                          value={profile?.data?.data?.phone}
                          edit={edit}
                          control={control}
                          disabled={true}
                          emptyDisplayText="Add"
                        />
                        <IconButton
                          size="small"
                          onClick={() => setOpenChangePhone(true)}
                          aria-label="Change phone number"
                          sx={{
                            position: "absolute",
                            right: { xs: 0, sm: 12 },
                            top: 36,
                            color: "primary.main",
                            bgcolor: "rgba(255, 170, 0, 0.08)",
                            "&:hover": { bgcolor: "rgba(255, 170, 0, 0.15)" },
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>

              {/* Security & Access Card */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 6,
                  bgcolor: "background.paper",
                  boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.04)",
                  border: "1px solid", borderColor: "divider",
                }}
              >
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "secondary.main", letterSpacing: "-0.5px" }}>
                    Security Settings
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Configure your credentials and manage active device sessions.
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Box
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      borderRadius: 4,
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      justifyContent: "space-between",
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: { xs: 2, sm: 0 },
                      bgcolor: "action.hover",
                      border: "1px dashed rgba(0,0,0,0.1)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 3,
                          bgcolor: "background.paper",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                          display: "flex",
                          flexShrink: 0,
                        }}
                      >
                        <Lock sx={{ color: "primary.main" }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Account Password
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Last updated a month ago
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      onClick={() => setOpenChangePassword(true)}
                      sx={{
                        borderRadius: 3,
                        textTransform: "none",
                        fontWeight: 700,
                        px: 3,
                        width: { xs: "100%", sm: "auto" },
                        flexShrink: 0,
                        alignSelf: { xs: "stretch", sm: "center" },
                        bgcolor: "secondary.main",
                        "&:hover": { bgcolor: "black" },
                        transition: "all 0.2s",
                        color: "white",
                      }}
                    >
                      Change
                    </Button>
                  </Box>

                  <Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, pl: 0.5 }}>
                        Active Sessions
                      </Typography>
                    </Box>
                    <Box sx={{ borderRadius: 5, overflow: "hidden", border: "1px solid rgba(0,0,0,0.08)" }}>
                      <ActiveSessions enabled={!edit} />
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <ChangePassword
        open={openChangePassword}
        refetch={refetch}
        setOpen={setOpenChangePassword}
      />

      <ChangePhone
        open={openChangePhone}
        phone={profile?.data?.data?.phone}
        refetch={refetch}
        setOpen={setOpenChangePhone}
      />

      <DeleteMyAccount
        signOut={signOutAll}
        openDeleteAccount={openDeleteAccount}
        setOpenDeleteAccount={setOpenDeleteAccount}
      />
    </Box>
  );
}

// Helper component for uniform fields
function InfoField({
  label,
  name,
  value,
  edit,
  control,
  rules,
  error,
  disabled,
  select,
  options,
  placeholder,
  type,
  emptyDisplayText = "Not provided",
}) {
  const formatBirthDateForDisplay = (val) => {
    if (!val) return "";
    if (typeof val !== "string") return "";

    // Server uses "0001-01-01T00:00:00Z" as "unset" sentinel
    if (val === "0001-01-01T00:00:00Z" || val.startsWith("0001-01-01")) return "";

    // Display only the date part (avoid timezone shifting)
    const match = val.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : "";
  };

  const displayValue = name === "birth_date" ? formatBirthDateForDisplay(value) : value;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          ml: 0.5,
          mb: 0.8,
          display: "block",
          color: "secondary.main",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          fontSize: "10px",
          opacity: 0.8,
        }}
      >
        {label}
      </Typography>
      {edit ? (
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              size="small"
              placeholder={placeholder}
              type={type}
              InputLabelProps={type === "date" ? { shrink: true } : undefined}
              error={!!error}
              helperText={error?.message}
              disabled={disabled}
              select={select}
              SelectProps={select ? { displayEmpty: true } : undefined}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  bgcolor: disabled ? "action.hover" : "background.paper",
                  fontSize: "14px",
                  fontWeight: 500,
                  "& fieldset": { borderColor: "divider" },
                  "&:hover fieldset": { borderColor: "primary.main" },
                  "&.Mui-focused fieldset": { borderWidth: "2px" },
                },
              }}
            >
              {select &&
                options?.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "14px" }}>
                    {opt.label}
                  </MenuItem>
                ))}
            </TextField>
          )}
        />
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 1.8,
            bgcolor: disabled ? "action.hover" : "background.paper",
            border: "1px solid", borderColor: "divider",
            borderRadius: 3,
            minHeight: 48,
            display: "flex",
            alignItems: "center",
            boxShadow: "0 2px 4px rgba(0,0,0,0.02) inset",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: displayValue ? "text.primary" : "text.disabled" }}
          >
            {displayValue || emptyDisplayText}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

// Change Password Component
function ChangePassword(props) {
  // USESTATE & USEREF
  const [showPassword, setShowPassword] = useState(false);
  const [inputType, setInputType] = useState("password");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [confirmInputType, setConfirmInputType] = useState("password");

  const password = useRef({});

  // USEFORM HOOK
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    mode: "onChange",
  });

  password.current = watch("new_password", "");

  // CUSTOM HOOK
  const axiosPrivate = useAxiosPrivate();

  // SNACKBAR
  const { enqueueSnackbar } = useSnackbar();

  const { mutate, isLoading, data, error } = useMutation((password) => {
    return axiosPrivate.patch(`/profile/password`, password);
  });

  // HELPERS
  const onSubmit = (formData) => {
    mutate(formData);
  };
  const handleClick = () => {
    props.setOpen(false);
  };

  // USEFFECT
  useEffect(() => {
    if (data) {
      enqueueSnackbar(`Succesfully changed your password.`, {
        variant: "success",
        preventDuplicate: true,
        autoHideDuration: 2000,
      });
      props.setOpen(false);
      props.refetch();
    }
  }, [data, enqueueSnackbar, props]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(
        error?.response?.data?.error?.message ||
        error?.message ||
        "Network Error!",
        {
          variant: "error",
          preventDuplicate: true,
          autoHideDuration: 2000,
        }
      );
    }
  }, [enqueueSnackbar, error]);

  // RENDER
  return (
    <Dialog
      open={props.open}
      onClose={handleClick}
      PaperProps={{
        sx: {
          borderRadius: 6,
          boxShadow: "0px 20px 60px rgba(0, 0, 0, 0.15)",
          p: { xs: 1, sm: 2 },
        },
      }}
    >
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 4,
            px: 1,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderRadius: 4,
              bgcolor: "primary.main",
              display: "flex",
              boxShadow: "0 8px 16px rgba(255, 170, 0, 0.25)",
              mb: 2,
            }}
          >
            <Lock sx={{ color: "white", fontSize: 28 }} />
          </Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: "secondary.main", letterSpacing: "-0.5px", mb: 0.5 }}
          >
            Update Password
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", textAlign: "center", mb: 4, px: 2 }}
          >
            Please enter your current password and choose a new secure one.
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{ width: "100%" }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Controller
                name="old_password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    type={inputType}
                    label="Current Password"
                    error={!!errors?.old_password}
                    helperText={errors?.old_password?.message}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3.5,
                        "&.Mui-focused fieldset": { borderWidth: "2px" },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setShowNewPassword(!showNewPassword);
                              setInputType(showNewPassword ? "password" : "text");
                            }}
                          >
                            {showNewPassword ? (
                              <Visibility sx={{ fontSize: 18 }} />
                            ) : (
                              <VisibilityOff sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Divider sx={{ my: 0.5, opacity: 0.6 }} />

              <Controller
                name="new_password"
                control={control}
                rules={{
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "At least 6 characters required",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    type={inputType}
                    label="New Password"
                    error={!!errors?.new_password}
                    helperText={errors?.new_password?.message}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3.5,
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setShowPassword(!showPassword);
                              setInputType(showPassword ? "password" : "text");
                            }}
                          >
                            {showPassword ? (
                              <Visibility sx={{ fontSize: 18 }} />
                            ) : (
                              <VisibilityOff sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Controller
                name="confirm_password"
                control={control}
                rules={{
                  validate: (value) =>
                    value === password.current || "Passwords do not match",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    type={confirmInputType}
                    label="Confirm New Password"
                    error={!!errors?.confirm_password}
                    helperText={errors?.confirm_password?.message}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3.5,
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setShowConfirmPassword(!showConfirmPassword);
                              setConfirmInputType(showConfirmPassword ? "password" : "text");
                            }}
                          >
                            {showConfirmPassword ? (
                              <Visibility sx={{ fontSize: 18 }} />
                            ) : (
                              <VisibilityOff sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            <Box
              sx={{
                mt: 4,
                display: "flex",
                gap: 2,
              }}
            >
              <Button
                fullWidth
                variant="outlined"
                color="inherit"
                onClick={handleClick}
                sx={{
                  borderRadius: 3.5,
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.2,
                  borderColor: "divider",
                }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={isLoading}
                sx={{
                  borderRadius: 3.5,
                  textTransform: "none",
                  fontWeight: 700,
                  py: 1.2,
                  boxShadow: "0 4px 12px rgba(255, 170, 0, 0.2)",
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
    </Dialog>
  );
}

// Change Phone Component
function ChangePhone(props) {
  // USESTATE & USEREF
  const [openOtp, setOpenOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const phoneRef = useRef();

  const { currentTenant, tenantSessions, getTenantIdFromUrl } =
    useContext(AuthContext);
  // Some routes (edit profile) may not carry `tenant_id` in the URL.
  // Fall back to the latest known tenant session.
  const tenantId =
    currentTenant?.id ||
    tenantSessions?.[tenantSessions.length - 1]?.tenant_id ||
    getTenantIdFromUrl();

  // USEFORM HOOK
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "onChange",
  });

  // CUSTOM HOOK
  const axiosPrivate = useAxiosPrivate();

  // SNACKBAR
  const { enqueueSnackbar } = useSnackbar();

  // QUERY & MUTATION
  const {
    isLoading: otpLoading,
    data: otpData,
    error: otpError,
    refetch,
  } = useQuery(
    ["otp"],
    () => {
      return axios.get("/otp", {
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          type: "signup",
          phone: phoneRef.current,
          ...(tenantId && { tenant_id: tenantId }),
        },
      });
    },
    {
      refetchOnWindowFocus: false,
      enabled: false,
      retry: false,
    }
  );
  const { mutate, isLoading, data, error } = useMutation((phone) => {
    return axiosPrivate.patch(`/profile/phone`, phone);
  });

  // HELPERS
  const getOtp = (phoneData) => {
    phoneRef.current = phoneData.phone;
    refetch();
    setOpenOtp(true);
  };
  const onSubmit = (formData) => {
    setOpenOtp(false);
    mutate(formData);
  };
  const handleClick = () => {
    props.setOpen(false);
  };

  // USEFFECT// USEFFECT
  useEffect(() => {
    if (props.phone) {
      setValue("phone", props.phone);
    }
  }, [setValue, props]);

  useEffect(() => {
    if (otpData && otpSent) {
      enqueueSnackbar(`Code sent to your phone!`, {
        variant: "success",
        preventDuplicate: true,
        autoHideDuration: 2000,
      });
      setOtpSent(false);
    }
  }, [enqueueSnackbar, otpData, data, otpSent]);

  useEffect(() => {
    if (data) {
      enqueueSnackbar(`Succesfully changed your phone.`, {
        variant: "success",
        preventDuplicate: true,
        autoHideDuration: 2000,
      });
      setOpenOtp(false);
      props.setOpen(false);
      props.refetch();
    }
  }, [data, enqueueSnackbar, props]);

  useEffect(() => {
    if (error) {
      if (error?.response?.data?.error?.field_error?.length > 0) {
        error?.response?.data?.error?.field_error?.map((msg) => {
          return enqueueSnackbar(msg.description || "Network Error!", {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 2000,
          });
        });
      } else {
        enqueueSnackbar(
          error?.response?.data?.error?.message ||
          error?.message ||
          "Network Error!",
          {
            variant: "error",
          }
        );
      }
    }
    if (otpError) {
      enqueueSnackbar(
        otpError?.response?.data?.error?.message ||
        error?.message ||
        "Network Error!",
        {
          variant: "error",
          preventDuplicate: true,
          autoHideDuration: 2000,
        }
      );
    }
  }, [enqueueSnackbar, error, otpError]);

  // RENDER
  return (
    <Dialog
      open={props.open}
      onClose={handleClick}
      PaperProps={{
        sx: {
          borderRadius: 6,
          boxShadow: "0px 20px 60px rgba(0, 0, 0, 0.15)",
          p: { xs: 1, sm: 2 },
        },
      }}
    >
      {!(openOtp && !otpLoading && !otpError) ? (
        <Container component="main" maxWidth="xs">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 4,
              px: 1,
            }}
          >
            <Box
              sx={{
                p: 2,
                borderRadius: 4,
                bgcolor: "primary.main",
                display: "flex",
                boxShadow: "0 8px 16px rgba(255, 170, 0, 0.25)",
                mb: 2,
              }}
            >
              <Edit sx={{ color: "white", fontSize: 28 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "secondary.main", letterSpacing: "-0.5px", mb: 0.5 }}
            >
              Verify New Phone
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", textAlign: "center", mb: 4, px: 2 }}
            >
              We'll send a verification code to your new number.
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmit(getOtp)}
              sx={{ width: "100%" }}
            >
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: "Phone number is required",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Phone Number"
                    placeholder="e.g. +1234567890"
                    autoComplete="phone"
                    error={!!errors?.phone}
                    helperText={errors?.phone?.message}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3.5,
                      },
                      mb: 2,
                    }}
                  />
                )}
              />
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  color="inherit"
                  onClick={handleClick}
                  sx={{
                    borderRadius: 3.5,
                    textTransform: "none",
                    fontWeight: 600,
                    py: 1.2,
                    borderColor: "divider",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={otpLoading && openOtp}
                  sx={{
                    borderRadius: 3.5,
                    textTransform: "none",
                    fontWeight: 700,
                    py: 1.2,
                    boxShadow: "0 4px 12px rgba(255, 170, 0, 0.2)",
                  }}
                >
                  {otpLoading && openOtp ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Send Code"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
      ) : (
        <Container component="main" maxWidth="xs">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 4,
              px: 1,
            }}
          >
            <Box
              sx={{
                p: 2,
                borderRadius: 4,
                bgcolor: "secondary.main",
                display: "flex",
                boxShadow: "0 8px 16px rgba(0, 0, 0, 0.15)",
                mb: 2,
              }}
            >
              <Lock sx={{ color: "white", fontSize: 28 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "secondary.main", letterSpacing: "-0.5px", mb: 0.5 }}
            >
              Confirm Identity
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", textAlign: "center", mb: 4, px: 2 }}
            >
              A code was sent to <strong>{phoneRef.current}</strong>. Please enter it below.
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              sx={{ width: "100%" }}
            >
              <Controller
                name="otp"
                control={control}
                rules={{
                  required: "Verification code is required",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Verification Code"
                    placeholder="Enter 6-digit code"
                    autoComplete="otp"
                    autoFocus
                    error={!!errors?.otp}
                    helperText={errors?.otp?.message}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3.5,
                      },
                      mb: 2,
                    }}
                  />
                )}
              />
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  color="inherit"
                  onClick={() => setOpenOtp(false)}
                  sx={{
                    borderRadius: 3.5,
                    textTransform: "none",
                    fontWeight: 600,
                    py: 1.2,
                    borderColor: "divider",
                  }}
                >
                  Back
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={isLoading}
                  sx={{
                    borderRadius: 3.5,
                    textTransform: "none",
                    fontWeight: 700,
                    py: 1.2,
                    boxShadow: "0 4px 12px rgba(255, 170, 0, 0.2)",
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Verify & Update"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
      )}
    </Dialog>
  );
}
export default Profile;
